import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Search, Video, Image as ImageIcon, FileText, Download, X, Play,
  ChevronDown, HelpCircle, GraduationCap, Loader2,
} from "lucide-react";
import { api, mensajeError } from "../api/client";

type TipoTutorial = "VIDEO" | "IMAGEN" | "PDF";

interface Tutorial {
  id: string;
  titulo: string;
  descripcion: string;
  categoria: string;
  tipo: TipoTutorial;
  url?: string | null;
  archivoBase64?: string | null;
  archivoNombre?: string | null;
}

interface PreguntaFrecuente {
  id: string;
  pregunta: string;
  respuesta: string;
}

// Convierte un enlace de YouTube o Vimeo (cualquier formato común que la
// gente pega: watch?v=, youtu.be/, vimeo.com/...) en la URL de embed que se
// puede meter directo en un <iframe>. Si no reconoce el formato, regresa
// null y se cae a mostrar un enlace normal en su lugar.
function urlEmbed(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com")) {
      const id = u.searchParams.get("v") || u.pathname.split("/").filter(Boolean).pop();
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (u.hostname.includes("youtu.be")) {
      const id = u.pathname.split("/").filter(Boolean).pop();
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (u.hostname.includes("vimeo.com")) {
      const id = u.pathname.split("/").filter(Boolean).pop();
      if (id) return `https://player.vimeo.com/video/${id}`;
    }
    return null;
  } catch {
    return null;
  }
}

function miniaturaYoutube(url: string): string | null {
  const embed = urlEmbed(url);
  if (embed && embed.includes("youtube.com/embed/")) {
    const id = embed.split("/embed/")[1];
    return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
  }
  return null;
}

function TarjetaTutorial({ tutorial, onAbrir }: { tutorial: Tutorial; onAbrir: () => void }) {
  const miniatura = tutorial.tipo === "VIDEO" && tutorial.url ? miniaturaYoutube(tutorial.url) : null;

  return (
    <button
      type="button"
      onClick={onAbrir}
      className="group flex flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white text-left shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md"
    >
      <div className="relative flex h-40 items-center justify-center overflow-hidden bg-gradient-to-br from-blue-50 to-slate-50">
        {tutorial.tipo === "IMAGEN" && tutorial.archivoBase64 ? (
          <img src={tutorial.archivoBase64} alt={tutorial.titulo} className="h-full w-full object-cover" />
        ) : miniatura ? (
          <img src={miniatura} alt={tutorial.titulo} className="h-full w-full object-cover" />
        ) : tutorial.tipo === "VIDEO" ? (
          <Video size={36} className="text-blue-300" />
        ) : (
          <FileText size={36} className="text-blue-300" />
        )}
        {tutorial.tipo === "VIDEO" && (
          <span className="absolute inset-0 flex items-center justify-center bg-slate-900/20 opacity-0 transition-opacity group-hover:opacity-100">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-blue-600 shadow">
              <Play size={18} className="ml-0.5" />
            </span>
          </span>
        )}
        <span className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-lg bg-white/90 text-blue-600 shadow-sm">
          {tutorial.tipo === "VIDEO" ? <Video size={14} /> : tutorial.tipo === "IMAGEN" ? <ImageIcon size={14} /> : <FileText size={14} />}
        </span>
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-4">
        <span className="w-fit rounded-full bg-blue-50 px-2.5 py-0.5 text-[11px] font-medium text-blue-600">
          {tutorial.categoria}
        </span>
        <h3 className="font-display text-base text-slate-800">{tutorial.titulo}</h3>
        <p className="line-clamp-2 text-sm text-slate-500">{tutorial.descripcion}</p>
      </div>
    </button>
  );
}

function ModalTutorial({ tutorial, onCerrar }: { tutorial: Tutorial; onCerrar: () => void }) {
  const embed = tutorial.tipo === "VIDEO" && tutorial.url ? urlEmbed(tutorial.url) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" onClick={onCerrar}>
      <div
        className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
          <h3 className="font-display text-lg text-slate-800">{tutorial.titulo}</h3>
          <button onClick={onCerrar} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>
        <div className="max-h-[75vh] overflow-y-auto p-5">
          {tutorial.tipo === "VIDEO" && embed && (
            <div className="aspect-video w-full overflow-hidden rounded-xl bg-black">
              <iframe
                src={embed}
                title={tutorial.titulo}
                className="h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
          {tutorial.tipo === "VIDEO" && !embed && tutorial.url && (
            <a href={tutorial.url} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline">
              Ver video en {tutorial.url}
            </a>
          )}
          {tutorial.tipo === "IMAGEN" && tutorial.archivoBase64 && (
            <img src={tutorial.archivoBase64} alt={tutorial.titulo} className="mx-auto max-h-[60vh] rounded-xl object-contain" />
          )}
          {tutorial.tipo === "PDF" && tutorial.archivoBase64 && (
            <div className="flex flex-col items-center gap-4 py-8">
              <FileText size={48} className="text-blue-300" />
              <a
                href={tutorial.archivoBase64}
                download={tutorial.archivoNombre || `${tutorial.titulo}.pdf`}
                className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                <Download size={15} /> Descargar PDF
              </a>
            </div>
          )}
          <p className="mt-4 text-sm text-slate-600">{tutorial.descripcion}</p>
        </div>
      </div>
    </div>
  );
}

function ItemFaq({ pregunta }: { pregunta: PreguntaFrecuente }) {
  const [abierta, setAbierta] = useState(false);
  return (
    <div className="rounded-2xl border border-slate-100 bg-white">
      <button
        type="button"
        onClick={() => setAbierta((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
      >
        <span className="font-medium text-slate-700">{pregunta.pregunta}</span>
        <ChevronDown size={18} className={`shrink-0 text-slate-400 transition-transform ${abierta ? "rotate-180" : ""}`} />
      </button>
      {abierta && <div className="px-5 pb-4 text-sm leading-relaxed text-slate-500">{pregunta.respuesta}</div>}
    </div>
  );
}

export default function Tutoriales() {
  const [tutoriales, setTutoriales] = useState<Tutorial[]>([]);
  const [preguntas, setPreguntas] = useState<PreguntaFrecuente[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState("");
  const [categoria, setCategoria] = useState<string>("Todos");
  const [tutorialAbierto, setTutorialAbierto] = useState<Tutorial | null>(null);

  useEffect(() => {
    api
      .get("/tutoriales")
      .then((res) => {
        setTutoriales(res.data.tutoriales);
        setPreguntas(res.data.preguntasFrecuentes);
      })
      .catch((err) => setError(mensajeError(err)))
      .finally(() => setCargando(false));
  }, []);

  const categorias = useMemo(() => {
    const set = new Set(tutoriales.map((t) => t.categoria));
    return ["Todos", ...Array.from(set)];
  }, [tutoriales]);

  const tutorialesFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return tutoriales.filter((t) => {
      const coincideCategoria = categoria === "Todos" || t.categoria === categoria;
      const coincideBusqueda =
        !q || t.titulo.toLowerCase().includes(q) || t.descripcion.toLowerCase().includes(q) || t.categoria.toLowerCase().includes(q);
      return coincideCategoria && coincideBusqueda;
    });
  }, [tutoriales, busqueda, categoria]);

  const preguntasFiltradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return preguntas;
    return preguntas.filter((p) => p.pregunta.toLowerCase().includes(q) || p.respuesta.toLowerCase().includes(q));
  }, [preguntas, busqueda]);

  return (
    <div className="min-h-screen bg-white text-slate-800">
      <header className="sticky top-0 z-40 border-b border-slate-100 bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-icono.png" alt="Mascotas Vet" className="h-9 w-9" />
            <span className="font-display text-xl text-slate-800">Mascotas</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-blue-600">
            <ArrowLeft size={15} /> Volver al inicio
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 to-blue-800 py-14 text-white">
        <div className="relative mx-auto max-w-3xl px-5 text-center">
          <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15">
            <GraduationCap size={26} />
          </span>
          <h1 className="font-display text-3xl sm:text-4xl">Tutoriales y ayuda</h1>
          <p className="mt-2 text-blue-100">
            Videos, guías e imágenes para sacarle el máximo provecho a Mascotas Vet, más respuestas a las dudas más
            comunes.
          </p>
          <div className="mx-auto mt-6 flex max-w-lg items-center gap-2 rounded-2xl bg-white p-2 shadow-lg">
            <Search size={18} className="ml-2 shrink-0 text-slate-400" />
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Busca un tema, por ejemplo: agenda, facturación, recordatorios..."
              className="w-full bg-transparent px-1 py-1.5 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none"
            />
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-6xl px-5 py-12">
        {error && <p className="mb-6 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}

        {cargando ? (
          <div className="flex justify-center py-20 text-slate-400">
            <Loader2 size={28} className="animate-spin" />
          </div>
        ) : (
          <>
            {/* Filtro de categorías */}
            {categorias.length > 1 && (
              <div className="mb-8 flex flex-wrap gap-2">
                {categorias.map((c) => (
                  <button
                    key={c}
                    onClick={() => setCategoria(c)}
                    className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
                      categoria === c
                        ? "border-blue-600 bg-blue-600 text-white"
                        : "border-slate-200 text-slate-500 hover:border-blue-200 hover:text-blue-600"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}

            {/* Galería de tutoriales */}
            {tutorialesFiltrados.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 py-14 text-center text-slate-400">
                {tutoriales.length === 0 ? "Todavía no hay tutoriales publicados." : "No encontramos tutoriales para tu búsqueda."}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {tutorialesFiltrados.map((t) => (
                  <TarjetaTutorial key={t.id} tutorial={t} onAbrir={() => setTutorialAbierto(t)} />
                ))}
              </div>
            )}

            {/* Preguntas frecuentes */}
            {preguntasFiltradas.length > 0 && (
              <section className="mt-16">
                <div className="mb-6 flex items-center gap-2">
                  <HelpCircle size={22} className="text-blue-600" />
                  <h2 className="font-display text-2xl text-slate-800">Preguntas frecuentes</h2>
                </div>
                <div className="space-y-3">
                  {preguntasFiltradas.map((p) => (
                    <ItemFaq key={p.id} pregunta={p} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      <footer className="border-t border-slate-100 py-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-5 text-sm text-slate-400 sm:flex-row">
          <div className="flex items-center gap-2">
            <img src="/logo-icono.png" alt="Mascotas Vet" className="h-7 w-7" />
            <span className="font-medium text-slate-600">Mascotas</span>
          </div>
          <p>© {new Date().getFullYear()} Mascotas. Todos los derechos reservados.</p>
          <div className="flex items-center gap-4">
            <Link to="/aviso-de-privacidad" className="hover:text-blue-600">Aviso de privacidad</Link>
            <a href="mailto:soporte@mascotas.vet" className="hover:text-blue-600">soporte@mascotas.vet</a>
          </div>
        </div>
      </footer>

      {tutorialAbierto && <ModalTutorial tutorial={tutorialAbierto} onCerrar={() => setTutorialAbierto(null)} />}
    </div>
  );
}
